import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError, apiClient } from '@/api/client';
import { setRealtimeToken } from '@/lib/supabase';
import type { AuthAnonResponse, AuthState, AuthTokenResponse } from '@/types';

/**
 * Manages the auth lifecycle for the candidate SPA.
 *
 * - On mount: calls `GET /api/auth/token` to check for an existing session.
 * - `ensureAuth()`: creates anonymous session via `POST /api/auth/anon` if needed.
 * - `getRealtimeToken()`: returns the in-memory token, refreshing if needed.
 */
export function useAuth() {
  const [auth, setAuth] = useState<AuthState>({
    isLoading: true,
    isAuthenticated: false,
    userId: null,
    accessToken: null,
  });
  const accessTokenRef = useRef<string | null>(null);
  const authRef = useRef(auth);
  authRef.current = auth;

  // Check for existing session on mount
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await apiClient<AuthTokenResponse>('/api/auth/token');
        if (cancelled) return;

        accessTokenRef.current = data.accessToken;
        await setRealtimeToken(data.accessToken);

        setAuth({
          isLoading: false,
          isAuthenticated: data.accessToken !== null,
          userId: data.userId,
          accessToken: data.accessToken,
        });
      } catch (err) {
        if (cancelled) return;
        // 401 = no session yet; anything else is a real error
        if (err instanceof ApiError && err.status === 401) {
          setAuth({
            isLoading: false,
            isAuthenticated: false,
            userId: null,
            accessToken: null,
          });
        } else {
          console.error('Auth check failed:', err);
          setAuth({
            isLoading: false,
            isAuthenticated: false,
            userId: null,
            accessToken: null,
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Create an anonymous session if the user doesn't have one.
   * Returns the auth state after ensuring authentication.
   */
  const ensureAuth = useCallback(async () => {
    if (authRef.current.isAuthenticated && accessTokenRef.current) {
      return {
        userId: authRef.current.userId!,
        accessToken: accessTokenRef.current,
      };
    }

    const data = await apiClient<AuthAnonResponse>('/api/auth/anon', {
      method: 'POST',
    });

    if (data.accessToken) {
      accessTokenRef.current = data.accessToken;
      await setRealtimeToken(data.accessToken);
    }

    setAuth({
      isLoading: false,
      isAuthenticated: data.accessToken !== null,
      userId: data.userId,
      accessToken: data.accessToken,
    });

    return {
      userId: data.userId,
      accessToken: data.accessToken,
    };
  }, []);

  /**
   * Get the current Realtime access token, refreshing from the backend if stale.
   */
  const getRealtimeToken = useCallback(async () => {
    if (accessTokenRef.current) {
      return accessTokenRef.current;
    }

    const data = await apiClient<AuthTokenResponse>('/api/auth/token');
    accessTokenRef.current = data.accessToken;
    await setRealtimeToken(data.accessToken);
    return data.accessToken;
  }, []);

  const updateAuth = useCallback(
    async (accessToken: string | null, userId: string | null) => {
      if (accessToken) {
        accessTokenRef.current = accessToken;
        await setRealtimeToken(accessToken);
      }

      setAuth({
        isLoading: false,
        isAuthenticated: !!accessToken,
        userId: userId,
        accessToken: accessToken,
      });
    },
    [],
  );

  return {
    auth,
    ensureAuth,
    getRealtimeToken,
    updateAuth,
  };
}
